import React, { useCallback, useMemo, useState } from "react";

import {
  isTestCasePolicy,
  ResultOptions,
  TestCaseList,
} from "@design-controls/types";
import {
  createQColumnHelper,
  DataProvider,
  Filtering,
  QDataTable,
  TableMenuItem,
  QDeleteConfirmationModal,
  QText,
  Pagination,
  QTag,
} from "@qualio/ui-components";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { useTestCaseList } from "../../hooks/designElement/useTestCaseList";
import { ShowOnlyOpen } from "../../filter/ShowOnlyOpen";
import { TestCaseType } from "./filter";
import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";
import { usePolicyGroupResourceQuickSearch } from "../../hooks/search/usePolicyGroupResourceQuickSearch";
import { CategoryFilter } from "../../filter/CategoryFilter";
import { TestResult } from "./filter/TestResult";

import { useDeleteTestCase } from "../../hooks/designElement/useDeleteTestCase";
import { GapTableCell } from "../../components/GapTableCell/GapTableCell";
import { designElementStatusColumn } from "../../components/DesignElementStatusColumn/DesignElementStatusColumn";
import { qriColumn } from "../../components/QRIColumn/QRIColumn";
import { filterByStatus, StatusFilter } from "../../filter/StatusFilter";
import useHasPermission from "../../hooks/useHasPermission";
import { UserPermissions } from "../../domain/permissions";
import { ResultValueToLabel } from "../../domain/resultLabel";
import { TestResultVariantColour } from "../../constants";
import { Link } from "react-router-dom";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";

const columnHelper = createQColumnHelper<TestCaseList>();

export const TestList: React.FC = () => {
  const { t } = useTranslation();
  const { isLoading, data } = useTestCaseList();
  const { id: productId } = useCurrentProduct();
  const location = useLocation();
  const { isLoading: isDeleting, mutate: deleteElement } = useDeleteTestCase();
  const [itemToDelete, setItemToDelete] = useState<TestCaseList | null>(null);
  const policies = useCurrentConfigs();
  const navigate = useNavigate();
  const permissions = useHasPermission([
    UserPermissions.EDIT_DESIGN_ELEMENTS,
    UserPermissions.DELETE_DESIGN_ELEMENTS,
  ]);

  const { isLoading: categoryIsLoading, data: categories } =
    usePolicyGroupResourceQuickSearch(isTestCasePolicy);

  const addTestResult = useCallback(
    (item: TestCaseList) => {
      navigate(`add-result/${item.id}${location.search}`);
    },
    [navigate, location],
  );

  const columns = useMemo(() => {
    return [
      columnHelper.code("code", {
        header: t("testCases.list.table.header.id"),
        maxWidth: "100px",
      }),
      {
        header: " ",
        meta: {
          width: "100px",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: TestCaseList };
        }) => <GapTableCell openIssues={row.policyIssues} type={row.type} />,
      },
      columnHelper.custom("title", {
        header: t("testCases.list.table.header.testCase"),
        maxWidth: "400px",
        enableSorting: true,
        cell: (props) => {
          return <Link to={props.row.id}>{props.getValue()}</Link>;
        },
      }),
      qriColumn(
        "category",
        (record) => record.category?.label,
        t("testCases.list.table.header.category"),
      ),
      columnHelper.date((record) => new Date(record.updated), {
        header: t("testCases.list.table.header.lastModified"),
        id: "updated",
        maxWidth: "150px",
      }),
      {
        header: t("testCases.list.table.header.result"),
        meta: {
          maxWidth: "150px",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: TestCaseList };
        }) => {
          if (!row.testResult) {
            return "";
          }

          return (
            <QTag
              variantColor={
                TestResultVariantColour[row.testResult.result as never]
              }
            >
              {ResultValueToLabel[row.testResult.result as never]}
            </QTag>
          );
        },
      },
      designElementStatusColumn(),
      columnHelper.menu({
        items: (
          <>
            <TableMenuItem onClick={addTestResult} data-cy="add-test-result">
              {t("testCases.list.actions.addTestResult")}
            </TableMenuItem>
            <TableMenuItem
              onClick={(item: TestCaseList) => navigate(`${item.id}/edit`)}
              isDisabled={(item: TestCaseList) =>
                !["product-development", "qualio"].includes(item.source)
              }
              isHidden={() =>
                !permissions[UserPermissions.EDIT_DESIGN_ELEMENTS]
              }
              data-cy="edit"
            >
              {t("testCases.list.actions.edit")}
            </TableMenuItem>
            <TableMenuItem
              onClick={(item: TestCaseList) => setItemToDelete(item)}
              color="red.500"
              data-cy="delete"
              isHidden={() =>
                !permissions[UserPermissions.DELETE_DESIGN_ELEMENTS]
              }
            >
              {t("testCases.list.actions.delete")}
            </TableMenuItem>
          </>
        ),
      }),
    ];
  }, [permissions, t, addTestResult, navigate]);

  const filterDefinitions = useMemo(() => {
    return {
      type: {
        label: t("testCases.list.filters.testType"),
        schema: Filtering.schemas.StringSchema().array().nullish(),
        activeRender: (v: any) => {
          const item = policies?.find((c) => c.type === v);
          if (!item) {
            return;
          }

          return {
            label: t("testCases.list.filterLabels.testType"),
            value: item.label,
          };
        },
      },
      category: {
        label: t("testCases.list.filters.category"),
        schema: Filtering.schemas.StringSchema().array().nullish(),
        activeRender: (v: any) => {
          const item = categories?.find((c) => c.identifiers.qri === v);
          if (!item) {
            return;
          }

          return {
            label: t("testCases.list.filterLabels.category"),
            value: item.displayLabel,
          };
        },
      },
      status: {
        label: t("testCases.list.filters.status"),
        schema: Filtering.schemas.StringSchema().array().nullish(),
      },
      testResult: {
        label: t("testCases.list.filters.latestResults"),
        schema: Filtering.schemas.StringSchema().nullish(),
        activeRender: (v: any) => ({
          label: t("testCases.list.filterLabels.result"),
          value: v && ResultValueToLabel[v as ResultOptions],
        }),
      },
      show_outdated: {
        label: t("testCases.list.filters.showOutdatedResult"),
        schema: Filtering.schemas.BooleanSchema().nullish(),
        activeRender: (v: any) => ({
          label: null,
          value: v && t("testCases.list.filterLabels.outdatedResult"),
        }),
      },
      show_required_parent: {
        label: t("testCases.list.filters.showMissingRequirement"),
        schema: Filtering.schemas.BooleanSchema().nullish(),
        activeRender: (v: any) => ({
          label: null,
          value: v && t("testCases.list.filterLabels.missingRequirement"),
        }),
      },
      show_failing: {
        label: t("testCases.list.filters.showFailingTestCase"),
        schema: Filtering.schemas.BooleanSchema().nullish(),
        activeRender: (v: any) => ({
          label: null,
          value: v && t("testCases.list.filterLabels.failingTestCase"),
        }),
      },
    };
  }, [policies, categories, t]);

  const filterOption = useCallback(
    (
      item: Readonly<TestCaseList>,
      searchTerm: string | undefined,
      filters: Filtering.ResolvedFilters<typeof filterDefinitions> | undefined,
    ) => {
      if (
        searchTerm &&
        !item.title
          .toLocaleLowerCase()
          .includes(searchTerm.toLocaleLowerCase()) &&
        !item.category?.label
          .toLocaleLowerCase()
          .includes(searchTerm.toLocaleLowerCase()) &&
        !item.code.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
      ) {
        return false;
      }

      if (filters && Object.values(filters).some((field) => field.value)) {
        const {
          show_outdated,
          show_required_parent,
          show_failing,
          type,
          category,
          testResult,
          status,
        } = filters;

        return (
          (!show_outdated?.value || item.policyIssues.OUTDATED_TESTRESULT) &&
          (!show_required_parent?.value || item.policyIssues.REQUIRES_PARENT) &&
          (!show_failing?.value || item.policyIssues.FAILING_TEST) &&
          (!type?.value?.length || type.value.includes(item.type)) &&
          (!testResult?.value ||
            testResult.value === item.testResult?.result ||
            (testResult.value === "Not Tested" && !item.testResult)) &&
          (!status?.value?.length || filterByStatus(item, status.value)) &&
          (!category?.value?.length ||
            category.value.includes(item.category?.qri))
        );
      }

      return true;
    },
    [],
  ) as DataProvider.FilterOptionFn<TestCaseList>;

  return (
    <>
      <Pagination.Auto pageSizeKey="size" pageIndexKey="page" clientSide>
        <Filtering.FilterProvider
          definitions={filterDefinitions}
          searchTermKey="search"
          persistanceKey={`test-list-filters-${productId}`}
        >
          <DataProvider.Fixed
            data={data ?? []}
            isLoading={isLoading}
            filterOption={filterOption}
          >
            <QDataTable
              columns={columns}
              data-cy="test-list"
              getRowId={(row: TestCaseList) => row.id}
            >
              <Filtering.FormContent>
                <TestCaseType policies={policies} />
                <CategoryFilter
                  categories={categories}
                  isLoading={categoryIsLoading}
                />
                <TestResult />
                <StatusFilter />
                <ShowOnlyOpen type="testCase" />
              </Filtering.FormContent>
            </QDataTable>
          </DataProvider.Fixed>
        </Filtering.FilterProvider>
      </Pagination.Auto>
      <Outlet />
      {itemToDelete && (
        <QDeleteConfirmationModal
          title={t("testCases.delete.modalTitle")}
          onConfirm={() => {
            itemToDelete &&
              deleteElement(itemToDelete as any, {
                onSettled: () => setItemToDelete(null),
              });
          }}
          inProgress={isDeleting}
          onClose={() => setItemToDelete(null)}
          message={
            <QText fontSize="sm">
              {t("testCases.delete.confirmMessage", {
                testCaseCodeTitle: itemToDelete.codeTitle,
              })}
            </QText>
          }
        />
      )}
    </>
  );
};
